import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import { formatInBusinessTimezone } from '../utils/dateTimeHelper';
import MobileHeader from '../components/MobileHeader';

const AssignmentSubmitScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const item = location.state?.item;

  const [loading, setLoading] = useState(false);
  const [singleFile, setSingleFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [description, setDescription] = useState('');

  if (!item) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
        <MobileHeader title="Assignment Details" showBack={true} />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3 }}>
          <Typography sx={{ fontSize: 48, mb: 1.5 }}></Typography>
          <Typography sx={{ fontWeight: 'bold', fontSize: 16, mb: 1, color: '#dc3545', textAlign: 'center' }}>
            No assignment data found
          </Typography>
          <Button variant="contained" onClick={() => navigate('/assignments')} sx={{ mt: 2, borderRadius: 20 }}>
            Go Back
          </Button>
        </Box>
      </Box>
    );
  }

  const onDownload = (fileUrl) => {
    if (!fileUrl) { alert('No file available'); return; }
    window.open(fileUrl, '_blank');
  };

  const onSelectFile = (event) => {
    const file = event.target.files[0];
    if (file) setSingleFile(file);
  };

  const onUploadAssignment = async () => {
    if (!singleFile) { alert('Please select a file to upload'); return; }

    const formData = new FormData();
    formData.append('AssignmentId', item.id);
    formData.append('Description', description);
    formData.append('File1', singleFile);

    setLoading(true);
    try {
      const token = localStorage.getItem('userToken');
      await api.post('api/school-common/assignments/submit-assignment-student', formData, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'multipart/form-data',
        },
      });
      alert('Assignment uploaded successfully!');
      navigate('/assignments');
    } catch (error) {
      handleCatch(error);
      alert('Failed to upload assignment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader title="Assignment Details" showBack={true} />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pt: 1.5, pb: 3 }}>
        {/* Assignment info card */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            mb: 1.5,
          }}
        >
          {/* Red accent heading */}
          <Box sx={{ bgcolor: '#b22222', px: 2, py: 1.5 }}>
            <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: 15 }}>
              {item.title}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, mt: 0.3 }}>
              {item.subjectName}
            </Typography>
          </Box>

          <Box sx={{ px: 2, py: 1.5 }}>
            {item.description ? (
              <Typography sx={{ fontSize: 13, color: '#444', mb: 1.5, lineHeight: 1.5 }}>
                {item.description}
              </Typography>
            ) : null}

            <Divider sx={{ mb: 1.5 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <CalendarIcon sx={{ fontSize: 14, color: '#888' }} />
                <Typography sx={{ fontSize: 12, color: '#555' }}>
                  Assigned:{' '}
                  {formatInBusinessTimezone(
                    item.creationTime || item.updatedDate,
                    'dd MMM yyyy'
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <CalendarIcon sx={{ fontSize: 14, color: '#e74c3c' }} />
                <Typography sx={{ fontSize: 12, color: '#e74c3c', fontWeight: '600' }}>
                  Due: {formatInBusinessTimezone(item.toBeSubmittedOn, 'dd MMM yyyy')}
                </Typography>
              </Box>
              {item.teacherName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <PersonIcon sx={{ fontSize: 14, color: '#888' }} />
                  <Typography sx={{ fontSize: 12, color: '#555' }}>
                    By: {item.teacherName}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              {item.file1?.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => onDownload(item.file1)}
                  sx={{ borderRadius: 20, fontSize: 12, borderColor: '#b22222', color: '#b22222' }}
                >
                  Download
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<SendIcon />}
                onClick={() => setIsUploading(!isUploading)}
                sx={{ borderRadius: 20, fontSize: 12, bgcolor: '#b22222', '&:hover': { bgcolor: '#8b0000' } }}
              >
                {isUploading ? 'Cancel' : 'Submit'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Submit form */}
        {isUploading && (
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              px: 2,
              py: 2,
            }}
          >
            <Typography sx={{ fontWeight: 'bold', fontSize: 14, color: colors.BLUE, mb: 1.5 }}>
              Submit Your Answer
            </Typography>

            <TextField
              placeholder="Write your answer or comments (optional)"
              multiline
              rows={3}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="small"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />

            {/* File picker */}
            <input
              accept="application/pdf,image/*"
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={onSelectFile}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                size="small"
                startIcon={<AttachFileIcon />}
                sx={{ borderRadius: 20, mb: 1.5 }}
              >
                Select File
              </Button>
            </label>

            {singleFile && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={singleFile.name}
                  onDelete={() => setSingleFile(null)}
                  color="primary"
                  size="small"
                />
              </Box>
            )}

            <Button
              variant="contained"
              fullWidth
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              onClick={onUploadAssignment}
              disabled={loading || !singleFile}
              sx={{
                height: 46,
                borderRadius: 23,
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: 14,
                bgcolor: '#b22222',
                '&:hover': { bgcolor: '#8b0000' },
              }}
            >
              {loading ? 'Uploading...' : 'Upload Assignment'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AssignmentSubmitScreen;
